import { createContext, useCallback, useReducer, useEffect } from "react";
import { ReactTagManager } from "react-gtm-ts";
import reducer, {
  initialState,
  OnboardingReducerState,
} from "./onboardingReducer";
import { onboardingGraph } from "@/data/onboarding";
import Mixpanel from "@/services/tracking";
import { STORAGE_KEY } from "@/constants";
import {
  Answer,
  FlowType,
  FormattedAnswer,
  OnboardingStep,
} from "@/data/onboarding/types";
import { getFormattedAnswers } from "@/utils";
import { getFlowConfig } from "@/data/flows";

type OnboardingActions = {
  setStep: (slug: string) => void;
  setCurrentFlow: (flow: FlowType) => void;
  answerQuestion: (answer: Answer) => void;
  setPhysicalStats: (height: string, weight: string) => void;
  getOnboardingStepBySlug: (slug?: string) => OnboardingStep | undefined;
  getOnboardingStepById: (id: string) => OnboardingStep | undefined;
  setFreetextData: (name: string, data: string) => void;
  toggleOption: (toggledOption: string) => void;
  completeQuestionnaire: () => void;
  getAnswerRequestObject: () => FormattedAnswer[];
  resetAnswers: () => void;
};

type OnboardingContextValue = OnboardingReducerState & OnboardingActions;

export const OnboardingContext = createContext<
  OnboardingContextValue | undefined
>(undefined);

export const OnboardingProvider = ({ children }: React.PropsWithChildren) => {
  const [state, dispatch] = useReducer(
    reducer,
    localStorage.getItem(STORAGE_KEY)
      ? (JSON.parse(
          localStorage.getItem(STORAGE_KEY) || "{}",
        ) as OnboardingReducerState)
      : initialState,
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const getOnboardingStepBySlug = (slug?: string) => {
    if (!slug) {
      return;
    }

    const foundId = Object.keys(onboardingGraph).find(
      (id) => onboardingGraph[id].slug === slug,
    );

    if (foundId) {
      return onboardingGraph[foundId];
    }
  };

  const getOnboardingStepById = (id: string) => {
    return onboardingGraph[id];
  };

  const setStep = useCallback((slug: string) => {
    const step = getOnboardingStepBySlug(slug);
    if (step) {
      dispatch({ type: "setStep", stepId: step.id });
    }
  }, []);

  const answerQuestion = (answer: Answer) => {
    Mixpanel.track("AnswerQuestion", {
      question: onboardingGraph[state.currentQuestionId].text,
      answer: answer,
    });
    dispatch({ type: "answerQuestion", answer });
  };

  const setPhysicalStats = (height: string, weight: string) =>
    dispatch({ type: "setPhysicalStats", height, weight });

  const setFreetextData = (name: string, data: string) =>
    dispatch({ type: "setFreetextData", name, data });

  const completeQuestionnaire = () => {
    dispatch({ type: "completeQuestionnaire" });
    ReactTagManager.action({
      event: "web_onboarding_finish_questionnaire",
    });
  };

  const toggleOption = (toggledOption: string) =>
    dispatch({ type: "multiSelectToggle", toggledOption });

  const setCurrentFlow = (flow: FlowType) =>
    dispatch({ type: "setCurrentFlow", flow });

  const getAnswerRequestObject = () => {
    const formattedAnswers = getFormattedAnswers(state.answers);
    const flowConfig = getFlowConfig(state.currentOnboardingFlow);
    if (flowConfig?.formatAnswers) {
      return flowConfig.formatAnswers(formattedAnswers, state);
    }
    return formattedAnswers;
  };

  const resetAnswers = () => {
    localStorage.clear();
    dispatch({ type: "reset" });
  };

  const value = {
    ...state,
    setStep,
    setCurrentFlow,
    answerQuestion,
    setPhysicalStats,
    getOnboardingStepBySlug,
    getOnboardingStepById,
    setFreetextData,
    toggleOption,
    completeQuestionnaire,
    getAnswerRequestObject,
    resetAnswers,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
